import React, { useState, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faLocationDot } from '@fortawesome/pro-regular-svg-icons';
import { faEdit, faHistory } from '@fortawesome/pro-solid-svg-icons';
import cogoToast from 'cogo-toast';
import './ArtistModalTalentReport.scss';

import TalentResultStats from '../Talent/Elements/TalentResultStats';
import ArtistModalTalentReportAudienceStats from './Elements/ArtistModalTalentReportAudienceStats';
import Tooltip from '../General/Tooltip';

import { blockOnRequiringSubscription, isSubscribedToFeature } from '../../Helpers/subscription_helpers';
import { getGiftingRequestsForUser } from '../../Helpers/brand_helpers';
import { isAdminControlMode } from '../../Helpers/ui_helpers';
import { matchScrollHeight } from '../../Helpers/helpers';
import { getSmartArtistBio, getLookbookById, isSubscribedBrand } from '../../Helpers/user_helpers';
import { getRelativePrettyDate } from '../../Helpers/formatting';
import { updateUserAnalysis } from '../../APIClient/user_analysis';

const ArtistModalTalentReport = props => {
  const { ui, analytics, user, talent, details } = props;
  const { brands, tags, analysis, description, stats, managers, locationRaw } = details || {};
  const { summary_custom, summary_recommendation } = analysis || {};
  const parsedLocation = JSON.parse(locationRaw || '{}');
  const formattedLocationTag =
    parsedLocation.country === 'United States'
      ? [parsedLocation.city, parsedLocation.state].filter(Boolean).join(', ')
      : [parsedLocation.city, parsedLocation.country].filter(Boolean).join(', ');
  const showLocationTag = formattedLocationTag.length > 0;

  const isAgentTag = tag => tag.value.includes('Agent - ');
  const visibleTags = _.filter(tags, tag => {
    if (isAgentTag(tag)) return false;
    if (tag.isHidden) return false;
    if (tag.type === 'brandpromoter') return false;
    if (tag.type === 'domainpromoter') return false;
    if (tag.type === 'collaboration') return false;
    if (tag.type === 'social') return false; // Don't show social because now we visualize the stats
    if (tag.type === 'catalog') return false;
    if (tag.value.includes(' Manual')) return false;
    if (showLocationTag && tag.type === 'geo' && tag.isAutomated) return false;

    return true;
  });
  const requests = getGiftingRequestsForUser(analytics, talent);

  const [descriptionSummary, setDescriptionSummary] = useState(getSmartArtistBio(details));
  const [isEditingDescription, setIsEditingDescription] = useState(false);

  const hasAudienceStats = !!stats?.audienceDevices && !!stats?.audienceGeos;

  useEffect(() => {
    // description change on details prop change, switching artist in talent modal
    setDescriptionSummary(getSmartArtistBio(details));
  }, [details]);

  const handleEditToggle = async () => {
    if (isEditingDescription) {
      handleSaveDescription();
    } else {
      setIsEditingDescription(true);
    }
  };

  const cancelEditing = () => {
    setDescriptionSummary(getSmartArtistBio(details));
    setIsEditingDescription(false);
  };

  const performUpdate = async updates => {
    try {
      const updatedAnalysis = await updateUserAnalysis(analysis, updates);
      setDescriptionSummary(updatedAnalysis.summary_custom || updatedAnalysis.summary_recommendation || description);
      setIsEditingDescription(false);
      cogoToast.success('Description updated');
    } catch (e) {
      console.error(e);
      cogoToast.error('Error updating description, please try again.');
    }
  };

  const handleSaveDescription = async () => {
    const trimmedDescription = descriptionSummary.trim();

    // Ensure we need to update
    if (trimmedDescription === getSmartArtistBio(details)) return setIsEditingDescription(false);

    // If saving empty string, clear custom summary
    await performUpdate({ summary_custom: trimmedDescription || null });
  };

  const revertToSummaryRecommendation = async () => {
    await performUpdate({ summary_custom: null });
  };

  const showRevertSummaryButton = isEditingDescription && !!summary_custom && !!summary_recommendation;
  const adminSummaryExplainer =
    summary_custom && summary_custom === descriptionSummary && !!summary_recommendation
      ? 'This is a custom analysis written by our team, we also have an AI generated summary if you want to revert'
      : summary_custom && summary_custom === descriptionSummary && !summary_recommendation
      ? 'This is a custom analysis written by our team, we do not yet have an AI generated summary.'
      : !summary_custom && !summary_recommendation && description && description === descriptionSummary
      ? 'This is the user written bio, we do not yet have an AI generated summary. Either wait for one to come in or write a custom one.'
      : !summary_custom && !summary_recommendation && !description && descriptionSummary === getSmartArtistBio(details)
      ? `This is a complete fallback bio, meaning we don't have an AI bio, haven't written a custom bio, or don't have a bio written by the user. Either write one or wait for AI one.`
      : 'Click save to save your custom summary.';

  return (
    <div className='artist-modal-talent-report-container'>
      <div className='report-section'>
        <div className='section-header-section'>
          <div className='section-header'>{description === descriptionSummary ? 'Bio from Creator' : 'Description'}</div>
          {adminSummaryExplainer && isEditingDescription && <div className='section-subheader'>{adminSummaryExplainer}</div>}
        </div>
        {isAdminControlMode(ui) && (
          <div className='section-header-btns'>
            {isEditingDescription && (
              <div className='section-header-btn' onClick={cancelEditing}>
                CANCEL
              </div>
            )}
            {showRevertSummaryButton && (
              <Tooltip
                message={analysis.summary_recommendation}
                getIconDiv={() => (
                  <div className='section-header-btn' onClick={revertToSummaryRecommendation}>
                    <FontAwesomeIcon icon={faHistory} />
                    Revert to auto-generated
                  </div>
                )}
              ></Tooltip>
            )}
            <div className={cn('section-header-btn', { save: isEditingDescription })} onClick={handleEditToggle}>
              <FontAwesomeIcon icon={faEdit} />
              {isEditingDescription ? 'Save' : 'Edit'}
            </div>
          </div>
        )}
        <div className='section-body'>
          <div className={cn('analysis', { unsubscribed: !isSubscribedBrand(user) })}>
            {isEditingDescription ? (
              <textarea
                autoFocus
                rows={3}
                ref={matchScrollHeight}
                className='description-summary-textarea'
                disabled={!analysis}
                value={descriptionSummary}
                placeholder={analysis?.summary_recommendation || description || 'Enter Summary Here'}
                onChange={e => setDescriptionSummary(e.target.value)}
              />
            ) : (
              descriptionSummary
            )}
          </div>
        </div>
      </div>
      <div className='report-section'>
        <div className='section-header-section'>
          <div className='section-header'>Statistics</div>
        </div>
        <div className='section-body'>
          <TalentResultStats rows={isMobile ? 1 : 2} user={user} ui={ui} result={talent} />
        </div>
      </div>
      {!!brands?.length && (
        <div className='report-section'>
          <div className='section-header-section'>
            <div className='section-header'>Most Frequently Mentioned Brands</div>
          </div>
          <div className='section-body'>
            <div className='brands'>
              {brands.slice(0, 16).map(({ brand, count }) => {
                const canView = isSubscribedToFeature(user, 'ADVANCED_TALENT_ANALYTICS') || isAdminControlMode(ui);
                const blockIfNeeded = () => blockOnRequiringSubscription(user, 'ADVANCED_TALENT_ANALYTICS');
                return (
                  <div onClick={blockIfNeeded} key={brand} className={cn('brand', { hidden: !canView })}>
                    {canView ? brand : 'Locked'}
                    <div className='count'>{count}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {hasAudienceStats && <ArtistModalTalentReportAudienceStats stats={stats} user={user} ui={ui} />}
      {(!!visibleTags?.length || showLocationTag) && (
        <div className='report-section'>
          <div className='section-header-section'>
            <div className='section-header'>Talent Tags</div>
          </div>
          <div className='section-body'>
            <div className='tags'>
              {managers?.map(userManager => {
                const { managerCompany, managerPosition, name } = userManager.manager;
                const parts = _.filter([managerCompany, managerPosition, name]);
                return (
                  <div key={userManager.id} className='tag manager'>
                    <FontAwesomeIcon icon={faUsers} />
                    {parts.join(' • ')}
                  </div>
                );
              })}
              {showLocationTag && (
                <div className='tag location'>
                  <FontAwesomeIcon icon={faLocationDot} />
                  {formattedLocationTag}
                </div>
              )}
              {visibleTags.map(tag => {
                const { value, id } = tag;
                return (
                  <div key={id} className='tag'>
                    {value}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {!!requests.length && (
        <div className='report-section'>
          <div className='section-header-section'>
            <div className='section-header'>Gifting Requests Sent</div>
          </div>
          <div className='section-body'>
            <div className='requests'>
              {requests.map(request => {
                const { Lookbook_id, userAccepted, userRejected } = request;
                const lookbook = Lookbook_id && getLookbookById(user, Lookbook_id);
                return (
                  <div className='request' key={request.id}>
                    <div className='title'>{lookbook?.title || 'General Gifting'}</div>
                    {!userAccepted && !userRejected && <div className='status'>Sent {getRelativePrettyDate(request.createdAt)}</div>}
                    {!!userAccepted && <div className='status'>Accepted {getRelativePrettyDate(request.userAcceptedAt)}</div>}
                    {!!userRejected && <div className='status'>Dismissed {getRelativePrettyDate(request.userRejectedAt)}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ArtistModalTalentReport.propTypes = {
  ui: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  analytics: PropTypes.object.isRequired,
  talent: PropTypes.object.isRequired
};

export default ArtistModalTalentReport;
